/**
 * Text interface for agent clients (agent-api/server.mjs).
 *
 * An agent runs the real game client with drawing switched off (main.ts skips
 * post.render when window.__agent is set), so every rule is the real one. What
 * it cannot do is look at the canvas, so this module describes the live world
 * as text instead: exact numbers a human would have to eyeball, plus a small
 * raycast picture of what is in front of the player.
 *
 * Installed as window.__agentApi = { report(), state(), craft(name, n) }.
 */

import { BLOCKS, CRAFTING_TABLE_ID, IS_LAVA, IS_OPAQUE, IS_SOLID, IS_WATERY } from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import type { Inventory } from '../game/inventory';
import { allRecipes, countStacks, ingredientLines, planFill } from '../game/recipeBook';
import type { Stack } from '../game/types';
import { nameOf } from '../items';
import type { World } from '../world/chunk';

interface Vec { x: number; y: number; z: number }

export interface AgentDeps {
  world(): World;
  /** Eye position (the camera). */
  eye(): Vec;
  /** Feet position. */
  feet(): Vec;
  yaw(): number;
  pitch(): number;
  flags(): { onGround: boolean; inWater: boolean; flying: boolean };
  inventory: Inventory;
  stats: { health: number; food: number; dead: boolean };
  mobs(): Array<Vec & { type: string; hostile: boolean; health: number }>;
  items(): Array<Vec & { stack: Stack }>;
  players(): Array<Vec & { name: string }>;
  /** What the crosshair is on, as main.ts already words it. */
  look(): string;
  /** The block at the crosshair as [x, y, z, id], or null (nothing, or a mob). */
  target(): number[] | null;
  /** true while the player is parked waiting for its spawn chunk (after joining or respawning). */
  holding(): boolean;
  /** What happened to the player since the last call (damage, death), drained. */
  notes(): string[];
  /** How many of those are waiting (lets the server notice damage without draining them). */
  noteCount(): number;
  biome(): string;
  hours(): number;
  mode(): string;
  dimension(): string;
}

const COLS = 48, ROWS = 16, VIEW_DIST = 40;
const TAN_H = Math.tan((80 * Math.PI) / 360);
const TAN_V = TAN_H * (ROWS / COLS) * 2; // terminal cells are ~twice as tall as wide

/** One character per block kind, picked from its name. */
const glyphOf: string[] = [];
function glyph(id: number): string {
  let g = glyphOf[id];
  if (g) return g;
  const n = (BLOCKS[id]?.name ?? '').toLowerCase();
  g = IS_WATERY[id] ? '~'
    : /lava|fire|magma/.test(n) ? 'L'
    : /ore/.test(n) ? '*'
    : /leaves/.test(n) ? '%'
    : /log|wood|stem/.test(n) ? '|'
    : /plank|door|fence|chest|crafting|table|bookshelf|barrel|bed/.test(n) ? '='
    : /grass block|moss|podzol|mycelium/.test(n) ? '"'
    : /dirt|farmland|mud|path/.test(n) ? ':'
    : /sand|gravel/.test(n) ? '.'
    : /snow|ice|quartz|wool/.test(n) ? 'o'
    : /glass/.test(n) ? '\''
    : IS_OPAQUE[id] ? '#'
    : ','; // plants, torches and other small things
  glyphOf[id] = g;
  return g;
}

const LEGEND = '(blank sky, " grass, : dirt, # stone, . sand, ~ water, | log, % leaves, = built/wood, * ore, L lava, , plant, '
  + '@ player, H hostile mob, m animal, i item, + crosshair)';

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
/** Compass word for a horizontal offset; north is -z, east is +x (vanilla). */
function bearing(dx: number, dz: number): string {
  const a = Math.atan2(dx, -dz); // 0 = north, clockwise
  return COMPASS[((Math.round(a / (Math.PI / 4)) % 8) + 8) % 8];
}

/** "12m NE, 3 up": where something is relative to the player, in words. */
function where(from: Vec, to: Vec): string {
  const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
  const d = Math.hypot(dx, dz);
  const v = Math.abs(dy) < 1.5 ? '' : `, ${Math.round(Math.abs(dy))} ${dy > 0 ? 'up' : 'down'}`;
  return `${d < 1 ? 'here' : `${Math.round(d)}m ${bearing(dx, dz)}`}${v}`;
}

const at = (p: Vec): string => `${Math.floor(p.x)} ${Math.floor(p.y)} ${Math.floor(p.z)}`;

const BORING = /^(air|stone|dirt|grass block|deepslate|bedrock|granite|diorite|andesite|tuff|gravel|short grass|tall grass|grass|fern|large fern|water|flowing water)$|leaves/i;

export function installAgentApi(d: AgentDeps): void {
  /** First block along a ray, by 3-D DDA over the live world. */
  function cast(o: Vec, dx: number, dy: number, dz: number, max: number): { id: number; t: number } | null {
    const w = d.world();
    let x = Math.floor(o.x), y = Math.floor(o.y), z = Math.floor(o.z);
    const sx = dx > 0 ? 1 : -1, sy = dy > 0 ? 1 : -1, sz = dz > 0 ? 1 : -1;
    const tdx = Math.abs(1 / (dx || 1e-9)), tdy = Math.abs(1 / (dy || 1e-9)), tdz = Math.abs(1 / (dz || 1e-9));
    let tx = (dx > 0 ? x + 1 - o.x : o.x - x) * tdx, ty = (dy > 0 ? y + 1 - o.y : o.y - y) * tdy, tz = (dz > 0 ? z + 1 - o.z : o.z - z) * tdz;
    let t = 0;
    while (t <= max) {
      if (tx < ty && tx < tz) { x += sx; t = tx; tx += tdx; } else if (ty < tz) { y += sy; t = ty; ty += tdy; } else { z += sz; t = tz; tz += tdz; }
      if (y < 0 || y >= WORLD_HEIGHT) return null;
      const id = w.getBlock(x, y, z);
      if (id !== 0) return { id, t };
    }
    return null;
  }

  /** Camera basis: forward, right, up (see Player: forward = -sin yaw, -cos yaw). */
  function basis(): { f: Vec; r: Vec; u: Vec } {
    const yaw = d.yaw(), p = d.pitch(), cp = Math.cos(p);
    const f = { x: -Math.sin(yaw) * cp, y: Math.sin(p), z: -Math.cos(yaw) * cp };
    const r = { x: Math.cos(yaw), y: 0, z: -Math.sin(yaw) };
    const u = { x: r.y * f.z - r.z * f.y, y: r.z * f.x - r.x * f.z, z: r.x * f.y - r.y * f.x };
    return { f, r, u };
  }

  /** The scene in front of the player as COLS x ROWS characters. */
  function view(): string {
    const eye = d.eye(), { f, r, u } = basis();
    const grid: string[][] = [], depth: number[][] = [];
    for (let j = 0; j < ROWS; j++) {
      const row: string[] = [], dr: number[] = [];
      const v = (1 - (2 * (j + 0.5)) / ROWS) * TAN_V;
      for (let i = 0; i < COLS; i++) {
        const h = ((2 * (i + 0.5)) / COLS - 1) * TAN_H;
        const dx = f.x + r.x * h + u.x * v, dy = f.y + r.y * h + u.y * v, dz = f.z + r.z * h + u.z * v;
        const n = Math.hypot(dx, dy, dz);
        const hit = cast(eye, dx / n, dy / n, dz / n, VIEW_DIST);
        row.push(hit ? glyph(hit.id) : ' ');
        dr.push(hit ? hit.t : Infinity);
      }
      grid.push(row);
      depth.push(dr);
    }
    const put = (p: Vec, ch: string, lift: number): void => {
      const x = p.x - eye.x, y = p.y + lift - eye.y, z = p.z - eye.z;
      const fz = x * f.x + y * f.y + z * f.z;
      if (fz < 0.3) return;
      const i = Math.floor(((x * r.x + y * r.y + z * r.z) / (fz * TAN_H) + 1) * 0.5 * COLS);
      const j = Math.floor((1 - (x * u.x + y * u.y + z * u.z) / (fz * TAN_V)) * 0.5 * ROWS);
      if (i < 0 || i >= COLS || j < 0 || j >= ROWS) return;
      if (Math.hypot(x, y, z) > depth[j][i] + 0.5) return; // behind terrain
      grid[j][i] = ch;
    };
    for (const it of d.items()) put(it, 'i', 0.2);
    for (const m of d.mobs()) put(m, m.hostile ? 'H' : 'm', 0.8);
    for (const p of d.players()) put(p, '@', 1);
    grid[ROWS >> 1][COLS >> 1] = '+';
    return grid.map((row) => row.join('').replace(/\s+$/, '')).join('\n');
  }

  /** Nearest visible block of each interesting kind around the player. */
  function nearby(): string[] {
    const w = d.world(), p = d.feet();
    const px = Math.floor(p.x), py = Math.floor(p.y), pz = Math.floor(p.z);
    const best = new Map<number, { x: number; y: number; z: number; d2: number }>();
    const R = 12, RY = 6;
    for (let y = Math.max(0, py - RY); y <= Math.min(WORLD_HEIGHT - 1, py + RY); y++) {
      for (let z = pz - R; z <= pz + R; z++) for (let x = px - R; x <= px + R; x++) {
        const id = w.getBlock(x, y, z);
        if (id === 0 || BORING.test(BLOCKS[id]?.name ?? 'air')) continue;
        const d2 = (x - px) ** 2 + (y - py) ** 2 + (z - pz) ** 2;
        const b = best.get(id);
        if (b && b.d2 <= d2) continue;
        // only what a player could see: at least one side open to air/water/plants
        if (IS_OPAQUE[w.getBlock(x + 1, y, z)] && IS_OPAQUE[w.getBlock(x - 1, y, z)] && IS_OPAQUE[w.getBlock(x, y + 1, z)]
          && IS_OPAQUE[w.getBlock(x, y - 1, z)] && IS_OPAQUE[w.getBlock(x, y, z + 1)] && IS_OPAQUE[w.getBlock(x, y, z - 1)]) continue;
        best.set(id, { x, y, z, d2 });
      }
    }
    return [...best.entries()].sort((a, b) => a[1].d2 - b[1].d2).slice(0, 10)
      .map(([id, b]) => `${BLOCKS[id].name} at ${b.x} ${b.y} ${b.z} (${where(p, { x: b.x + 0.5, y: b.y, z: b.z + 0.5 })})`);
  }

  /** Closest tree trunk within 48 blocks, for when none is in the nearby list. */
  function nearestLog(): string | null {
    const w = d.world(), p = d.feet();
    const px = Math.floor(p.x), py = Math.floor(p.y), pz = Math.floor(p.z);
    let best: Vec | null = null, bd = Infinity, name = '';
    for (let z = pz - 48; z <= pz + 48; z += 2) for (let x = px - 48; x <= px + 48; x += 2) {
      const d2 = (x - px) ** 2 + (z - pz) ** 2;
      if (d2 >= bd) continue;
      for (let y = Math.max(1, py - 12); y <= Math.min(WORLD_HEIGHT - 1, py + 24); y++) {
        const id = w.getBlock(x, y, z);
        if (id !== 0 && / log$/i.test(BLOCKS[id]?.name ?? '')) { best = { x, y, z }; bd = d2; name = BLOCKS[id].name; break; }
      }
    }
    return best ? `${name} at ${best.x} ${best.y} ${best.z} (${where(p, { x: best.x + 0.5, y: best.y, z: best.z + 0.5 })})` : null;
  }

  /**
   * Ground heights around the player: the y of the top solid block of each
   * column, 13 x 13. What a person reads off the landscape at a glance, and
   * what a build site needs: equal numbers are flat ground.
   */
  function heights(): string {
    const w = d.world(), p = d.feet();
    const px = Math.floor(p.x), pz = Math.floor(p.z), top = Math.min(WORLD_HEIGHT - 1, Math.floor(p.y) + 12);
    const rows: string[] = [];
    for (let z = pz - 6; z <= pz + 6; z++) {
      const cells: string[] = [];
      for (let x = px - 6; x <= px + 6; x++) {
        let cell = ' ??';
        for (let y = top; y > top - 40 && y > 0; y--) {
          const id = w.getBlock(x, y, z);
          if (IS_WATERY[id]) { cell = ' ~~'; break; }
          if (IS_SOLID[id] && !/leaves|log/i.test(BLOCKS[id].name)) { cell = String(y).padStart(3); break; }
        }
        cells.push(x === px && z === pz ? cell.replace(/^ /, '>') : cell);
      }
      rows.push(`z=${String(z).padStart(4)}:${cells.join('')}`);
    }
    return `GROUND HEIGHT MAP (y of the top solid block; ~~ water; > you; columns are x=${px - 6}..${px + 6} left to right, west to east):\n${rows.join('\n')}`;
  }

  /**
   * A walkable route from the player to column (tx, tz): A* over standable
   * cells, moving one block north/south/east/west at a time, stepping up one
   * block (a jump), dropping up to two, wading through water at a cost and
   * never through lava. Returns the cells to walk through as [x, y, z] (feet),
   * ending at the target or, when it cannot be reached, at the closest point.
   */
  function path(tx: number, tz: number): { cells: number[][]; reached: boolean } | null {
    const w = d.world(), f = d.feet();
    const open = (x: number, y: number, z: number): boolean => { const id = w.getBlock(x, y, z); return !IS_SOLID[id] && !IS_LAVA[id]; };
    const stand = (x: number, y: number, z: number): boolean => {
      if (!open(x, y, z) || !open(x, y + 1, z)) return false;
      const below = w.getBlock(x, y - 1, z);
      return IS_SOLID[below] === 1 || IS_WATERY[w.getBlock(x, y, z)] === 1; // on ground, or swimming
    };
    const sx = Math.floor(f.x), sz = Math.floor(f.z);
    let sy = Math.floor(f.y + 0.01);
    for (let k = 0; k < 4 && !stand(sx, sy, sz); k++) sy--; // mid-jump: the cell we will land in
    if (!stand(sx, sy, sz)) return null;
    const R = 56, key = (x: number, y: number, z: number): number => ((x - sx + R) * 128 + (z - sz + R)) * 512 + y;
    const g = new Map<number, number>(), from = new Map<number, number>(), pos = new Map<number, number[]>();
    const h = (x: number, z: number): number => Math.abs(x - tx) + Math.abs(z - tz);
    const heap: Array<[number, number]> = []; // [f, key], binary min-heap
    const push = (fv: number, k: number): void => {
      heap.push([fv, k]);
      for (let i = heap.length - 1; i > 0;) { const p = (i - 1) >> 1; if (heap[p][0] <= heap[i][0]) break; [heap[p], heap[i]] = [heap[i], heap[p]]; i = p; }
    };
    const pop = (): number => {
      const top = heap[0][1], last = heap.pop()!;
      if (heap.length) {
        heap[0] = last;
        for (let i = 0; ;) {
          const l = i * 2 + 1, r = l + 1; let m = i;
          if (l < heap.length && heap[l][0] < heap[m][0]) m = l;
          if (r < heap.length && heap[r][0] < heap[m][0]) m = r;
          if (m === i) break;
          [heap[m], heap[i]] = [heap[i], heap[m]]; i = m;
        }
      }
      return top;
    };
    const k0 = key(sx, sy, sz);
    g.set(k0, 0); pos.set(k0, [sx, sy, sz]); push(h(sx, sz), k0);
    let best = k0, bestH = h(sx, sz), found = -1;
    for (let n = 0; heap.length && n < 30000; n++) {
      const k = pop(), [x, y, z] = pos.get(k)!;
      if (x === tx && z === tz) { found = k; break; }
      if (h(x, z) < bestH) { bestH = h(x, z); best = k; }
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = x + dx, nz = z + dz;
        if (Math.abs(nx - sx) > R || Math.abs(nz - sz) > R) continue;
        let ny = -1;
        if (stand(nx, y + 1, nz) && open(x, y + 2, z)) { if (!stand(nx, y, nz)) ny = y + 1; } // a step up needs headroom to jump
        if (stand(nx, y, nz)) ny = y;
        else if (ny < 0 && open(nx, y, nz) && open(nx, y + 1, nz)) {
          for (let dy = 1; dy <= 2; dy++) { if (!open(nx, y - dy, nz)) break; if (stand(nx, y - dy, nz)) { ny = y - dy; break; } }
        }
        if (ny < 0) continue;
        const nk = key(nx, ny, nz);
        const cost = g.get(k)! + 1 + (ny > y ? 0.6 : 0) + (ny < y ? 0.3 * (y - ny) : 0) + (IS_WATERY[w.getBlock(nx, ny, nz)] ? 3 : 0);
        if (cost >= (g.get(nk) ?? Infinity)) continue;
        g.set(nk, cost); from.set(nk, k); pos.set(nk, [nx, ny, nz]);
        push(cost + h(nx, nz), nk);
      }
    }
    const cells: number[][] = [];
    for (let k: number | undefined = found >= 0 ? found : best; k !== undefined && k !== k0; k = from.get(k)) cells.push(pos.get(k)!);
    return { cells: cells.reverse(), reached: found >= 0 };
  }

  /** The block under the player's feet: the ground level to build from. */
  function under(): string {
    const w = d.world(), p = d.feet();
    const x = Math.floor(p.x), z = Math.floor(p.z);
    for (let y = Math.floor(p.y - 0.01); y > Math.floor(p.y) - 4 && y >= 0; y--) {
      const id = w.getBlock(x, y, z);
      if (IS_SOLID[id]) return `${BLOCKS[id].name} at ${x} ${y} ${z} (so the first block above the ground here is y=${y + 1})`;
    }
    return 'nothing (you are in the air or in water)';
  }

  const hasTable = (): boolean => {
    const w = d.world(), p = d.feet();
    const px = Math.floor(p.x), py = Math.floor(p.y), pz = Math.floor(p.z);
    for (let y = py - 3; y <= py + 3; y++) for (let z = pz - 4; z <= pz + 4; z++) for (let x = px - 4; x <= px + 4; x++) {
      if (w.getBlock(x, y, z) === CRAFTING_TABLE_ID) return true;
    }
    return false;
  };

  const stackText = (s: Stack): string => `${s.count > 1 ? s.count + ' ' : ''}${nameOf(s.id) || 'item ' + s.id}`;

  function craftable(): string[] {
    const g = hasTable() ? 3 : 2, avail = countStacks(d.inventory.slots);
    const names = new Set<string>();
    for (const e of allRecipes()) if (planFill(e.recipe, g, g, avail)) names.add(e.name);
    return [...names];
  }

  /**
   * Craft by name, as the recipe book does: ingredients come out of the
   * inventory, the result goes in. 2x2 recipes anywhere, 3x3 ones need a
   * crafting table within reach.
   */
  function craft(name: string, times = 1): string {
    const q = name.trim().toLowerCase();
    if (!q) return 'craft: say what to craft, e.g. "craft oak planks"';
    const all = allRecipes();
    const matches = all.filter((e) => e.name.toLowerCase() === q);
    const pool = matches.length ? matches : all.filter((e) => e.name.toLowerCase().includes(q));
    if (!pool.length) return `craft: no recipe called "${name}"`;
    const g = hasTable() ? 3 : 2;
    let made = 0, last = pool[0];
    for (let n = 0; n < Math.max(1, Math.min(64, Math.floor(times))); n++) {
      const avail = countStacks(d.inventory.slots);
      let done = false;
      for (const e of pool) {
        last = e;
        const plan = planFill(e.recipe, g, g, avail);
        if (!plan || !d.inventory.canAdd(e.recipe.result.id, e.recipe.result.count)) continue;
        for (const id of plan.cells) if (id) d.inventory.remove(id, 1);
        d.inventory.add(e.recipe.result.id, e.recipe.result.count);
        made += e.recipe.result.count;
        done = true;
        break;
      }
      if (!done) break;
    }
    d.inventory.changed();
    if (made) return `crafted ${made} ${last.name}`;
    const needsTable = g === 2 && pool.every((e) => e.size === 3);
    return `craft: cannot make ${last.name}: ${needsTable ? 'needs a crafting table within 4 blocks' : 'missing ingredients'} (needs ${ingredientLines(last.recipe).join(', ')})`;
  }

  function report(): string {
    const p = d.feet(), fl = d.flags(), inv = d.inventory;
    const yawDeg = ((Math.round((-d.yaw() * 180) / Math.PI) % 360) + 360) % 360; // 0 = north, clockwise
    const hot = inv.slots.slice(0, 9).map((s, i) => `${i + 1}${i === inv.selected ? '*' : ''}:${s.count > 0 ? stackText(s) : '-'}`).join('  ');
    const bag = [...countStacks(inv.slots.slice(9))].map(([id, n]) => `${n} ${nameOf(id) || id}`).join(', ');
    // not the caves below: only what shares the player's level, give or take
    const mobs = d.mobs().filter((m) => Math.abs(m.y - p.y) <= 10).map((m) => ({ m, d: Math.hypot(m.x - p.x, m.z - p.z) })).filter((e) => e.d < 32).sort((a, b) => a.d - b.d).slice(0, 8);
    const items = d.items().map((m) => ({ m, d: Math.hypot(m.x - p.x, m.z - p.z) })).filter((e) => e.d < 16).sort((a, b) => a.d - b.d).slice(0, 6);
    const can = craftable();
    const near = nearby();
    const h = d.hours(), st = d.stats;
    const notes = d.notes();
    const tree = near.some((n) => /log/i.test(n)) ? null : nearestLog();
    const lines = [
      notes.length ? `WHAT HAPPENED: ${notes.join('; ')}` : null,
      d.holding() ? 'YOU ARE STILL ARRIVING (the ground is loading): send "wait 2" before acting' : null,
      `YOU: at ${p.x.toFixed(1)} ${p.y.toFixed(1)} ${p.z.toFixed(1)}, facing ${bearing(-Math.sin(d.yaw()), -Math.cos(d.yaw()))} (${yawDeg} deg), `
        + `pitch ${Math.round((d.pitch() * 180) / Math.PI)}${fl.inWater ? ', in water' : fl.flying ? ', flying' : fl.onGround ? '' : ', in the air'}`,
      `STATUS: health ${Math.ceil(st.health)}/20, food ${Math.round(st.food)}/20${st.dead ? ', DEAD (send "respawn")' : ''}, ${d.mode()}, `
        + `${d.biome()}, ${d.dimension()}, ${Math.floor(h)}:${String(Math.floor((h % 1) * 60)).padStart(2, '0')} ${h >= 6 && h < 18.5 ? 'day' : 'NIGHT'}`,
      `STANDING ON: ${under()}`,
      `LOOKING AT: ${d.look()}`,
      `HOTBAR (* = held): ${hot}`,
      bag ? `BACKPACK: ${bag}` : null,
      can.length ? `CAN CRAFT NOW: ${can.slice(0, 20).join(', ')}${can.length > 20 ? ', ...' : ''}` : null,
      d.players().length ? `PLAYERS: ${d.players().map((o) => `${o.name} at ${at(o)} (${where(p, o)})`).join('; ')}` : 'PLAYERS: nobody else online',
      mobs.length ? `MOBS: ${mobs.map(({ m }) => `${m.type}${m.hostile ? ' (HOSTILE)' : ''} at ${at(m)} (${where(p, m)})`).join('; ')}` : null,
      items.length ? `ITEMS ON GROUND (walk over to pick up): ${items.map(({ m }) => `${stackText(m.stack)} at ${at(m)} (${where(p, m)})`).join('; ')}` : null,
      near.length ? `NEARBY BLOCKS: ${near.join('; ')}` : null,
      tree ? `NEAREST TREE: ${tree}` : null,
      heights(),
      `VIEW ${LEGEND}:`,
      view(),
    ];
    return lines.filter((l): l is string => l !== null).join('\n');
  }

  (window as unknown as { __agentApi?: unknown }).__agentApi = {
    report, craft,
    state: () => {
      const f = d.feet(), h = d.inventory.held;
      return {
        pos: { x: f.x, y: f.y, z: f.z }, yaw: d.yaw(), pitch: d.pitch(), look: d.look(), target: d.target(),
        notes: d.noteCount(), health: d.stats.health,
        dead: d.stats.dead, holding: d.holding(), inWater: d.flags().inWater,
        items: d.inventory.slots.reduce((n, st) => n + st.count, 0),
        held: h.count > 0 ? { name: nameOf(h.id) || 'item ' + h.id, count: h.count } : null,
      };
    },
    /** Nearest player or mob whose name contains `q`: where to aim to hit it. */
    find: (q: string) => {
      const f = d.feet(), want = q.trim().toLowerCase();
      let best: { name: string; x: number; y: number; z: number; aim: number } | null = null, bd = Infinity;
      const consider = (name: string, o: Vec, aim: number): void => {
        if (!name.toLowerCase().includes(want)) return;
        const dist = Math.hypot(o.x - f.x, o.y - f.y, o.z - f.z);
        if (dist < bd) { bd = dist; best = { name, x: o.x, y: o.y, z: o.z, aim }; }
      };
      for (const o of d.players()) consider(o.name, o, 1.2);
      for (const m of d.mobs()) consider(m.type, m, 0.6);
      return best;
    },
    path,
    /** Block id at a world cell (0 = air). */
    block: (x: number, y: number, z: number) => d.world().getBlock(x, y, z),
    /** Does this cell hold something a block can be built against (not air, a plant or a liquid)? */
    solid: (x: number, y: number, z: number) => IS_SOLID[d.world().getBlock(x, y, z)] === 1,
    blockName: (id: number) => BLOCKS[id]?.name ?? 'block ' + id,
  };
}
